import { SortBy } from "shared/types";
import { cloneObject, randomID } from "shared/utils";

import {
  DEFAULT_FILTER_BUILDER_STATE,
  DEFAULT_FILTER_BUILDER_STATE_ANY,
} from "features/ui/Filters/FilterBuilder/constants";
import {
  ANY_ALL_IDS,
  AnyAll,
} from "features/ui/Filters/FilterBuilder/FilterGroup/AnyAllSelect";
import { EditableRowProperty } from "features/ui/Filters/FilterBuilder/FilterRow/types";
import {
  FilterBlockType,
  FilterGroupState,
  FilterRowState,
  FilterRowStateNotNull,
} from "features/ui/Filters/FilterBuilder/types";

export const getNextAnyAll = (parentAnyAll: AnyAll): AnyAll => {
  const currentIndex = ANY_ALL_IDS.findIndex((id) => id === parentAnyAll);
  const nextIndex = (currentIndex + 1) % ANY_ALL_IDS.length;

  return ANY_ALL_IDS[nextIndex];
};

const isRowOrGroupChildrenEmpty = (
  rowOrGroup: FilterRowState | FilterGroupState
) =>
  rowOrGroup.type === "group" &&
  (rowOrGroup as FilterGroupState).children.length == 0;

export const insertInGroupAfterID = (
  groupState: FilterGroupState,
  id: string,
  type: FilterBlockType
): FilterGroupState => {
  const newLayout = cloneObject(groupState);
  const newId = `${type}-${randomID()}`;

  if (groupState.id === id) {
    if (type === "row") {
      newLayout.children.splice(0, 0, {
        type: "row",
        id: newId,
        attribute: null,
        operator: null,
        values: [],
      });
    } else if (groupState.children.length === 0) {
      newLayout.children.splice(0, 0, {
        type: "group",
        id: newId,
        anyAll: "all",
        children: [
          {
            type: "row",
            id: `row-${randomID()}`,
            attribute: null,
            operator: null,
            values: [],
          },
        ],
      });
    }
  }

  newLayout.children.forEach((rowOrGroup, currentIndex) => {
    const { id: currentChildId, type: currentChildType } = rowOrGroup;

    // check .children of this group for the given id recursively
    if (currentChildType === "group" && currentChildId !== id) {
      newLayout.children[currentIndex] = insertInGroupAfterID(
        rowOrGroup as FilterGroupState,
        id,
        type
      );

      return;
    }

    // we found the row/group to insert after
    if (currentChildId === id) {
      const newIndex = currentIndex + 1;

      if (type === "row") {
        if (isRowOrGroupChildrenEmpty(rowOrGroup)) {
          (rowOrGroup as FilterGroupState).children = [
            {
              type: "row",
              id: newId,
              attribute: null,
              operator: null,
              values: [],
            },
          ];
        } else {
          newLayout.children.splice(newIndex, 0, {
            type: "row",
            id: newId,
            attribute: null,
            operator: null,
            values: [],
          });
        }
      } else if (type === "group") {
        const parentAnyAll = newLayout.anyAll;
        if (isRowOrGroupChildrenEmpty(rowOrGroup)) {
          (rowOrGroup as FilterGroupState).children = [
            {
              type: "group",
              id: newId,
              // If parent group is "any", then the new group default should be "all" and vice versa
              anyAll: getNextAnyAll(parentAnyAll),
              // Always add an empty row as well
              children: [
                {
                  type: "row",
                  id: `row-${randomID()}`,
                  attribute: null,
                  operator: null,
                  values: [],
                },
              ],
            },
          ];
        } else {
          newLayout.children.splice(newIndex, 0, {
            type: "group",
            id: newId,
            // If parent group is "any", then the new group default should be "all" and vice versa
            anyAll: getNextAnyAll(parentAnyAll),
            // Always add an empty row as well
            children: [
              {
                type: "row",
                id: `row-${randomID()}`,
                attribute: null,
                operator: null,
                values: [],
              },
            ],
          });
        }
      }

      return;
    }
  });

  return newLayout;
};

export const deleteFromGroupByID = (
  groupState: FilterGroupState,
  id: string
): FilterGroupState => {
  const newLayout = cloneObject(groupState);

  newLayout.children.forEach((rowOrGroup, currentIndex) => {
    const { id: currentChildId, type: currentChildType } = rowOrGroup;

    // check .children of this group for the given id recursively
    if (currentChildType === "group" && currentChildId !== id) {
      newLayout.children[currentIndex] = deleteFromGroupByID(
        rowOrGroup as FilterGroupState,
        id
      );

      return;
    }

    // delete this child but only if it's not the last one in the group
    if (currentChildId === id) {
      newLayout.children.splice(currentIndex, 1);

      return;
    }

    // if it's the last one in the group clear it (set it to an empty
    // filter row as in DEFAULT_FILTER_BUILDER_STATE const) and
    // give it unique id since it can be in a nested group
    if (currentChildId === id && newLayout.children.length === 1) {
      const newId = `row-${randomID()}`;
      newLayout.children = [
        {
          type: "row",
          id: newId,
          attribute: null,
          operator: null,
          values: [],
        },
      ];

      return;
    }

    return;
  });

  return newLayout;
};

export const updateGroupAnyAllByID = (
  groupState: FilterGroupState,
  id: string,
  anyAll: AnyAll
): FilterGroupState => {
  if (groupState.id === id) {
    return { ...groupState, anyAll };
  }

  const updatedChildren = groupState.children.map((child) => {
    if (child.type === "group") {
      return updateGroupAnyAllByID(child as FilterGroupState, id, anyAll);
    }

    return child;
  });

  return { ...groupState, children: updatedChildren };
};

export const updateRowPropertyByID = (
  groupState: FilterGroupState,
  id: string,
  property: EditableRowProperty,
  value?: string | string[]
): FilterGroupState => {
  const updatedChildren = groupState.children.map((child) => {
    if (child.type === "group") {
      return updateRowPropertyByID(
        child as FilterGroupState,
        id,
        property,
        value
      );
    }

    if (child.id === id) {
      return { ...child, [property]: value };
    }

    return child;
  });

  return { ...groupState, children: updatedChildren };
};

/**
 * Runs a function on all row children of a given FilterGroupState and returns the FilterGroupState.
 * - If onlyTopLevel is true, it only runs the function on the top-level children.
 * - Note that this mutates the original FilterGroupState and doesn't deep-copy anything.
 *
 * Example use:
 * const newDateFilter = runOnAllRowChildren(filters, (row) => {
 *    if (row.attribute === DATE_ATTRIBUTE_SIGNAL_EVENTS) {
 *      row.attribute = WO_DATE_ATTRIBUTE;
 *    }
 * });
 */
export const runOnAllRowChildren = (
  filterState: FilterGroupState,
  rowFormatFunction: (row: FilterRowStateNotNull) => void,
  onlyTopLevel = false
): FilterGroupState => {
  const runOnAllFilterRowStates = (
    groupState: FilterGroupState
  ): FilterGroupState => {
    groupState.children.forEach((child) => {
      if (child.type === "group") {
        !onlyTopLevel && runOnAllFilterRowStates(child as FilterGroupState);
      } else {
        rowFormatFunction(child as FilterRowStateNotNull);
      }
    });

    return groupState;
  };

  return runOnAllFilterRowStates(filterState);
};

export const runOnAllFilterRowStatesWithAttribute = (
  attribute: string,
  filterState: FilterGroupState,
  rowFormatFunction: (row: FilterRowStateNotNull) => void,
  onlyTopLevel = false
): FilterGroupState => {
  const runOnAllFilterRowStates = (
    groupState: FilterGroupState
  ): FilterGroupState => {
    groupState.children.forEach((child) => {
      if (child.type === "group" && !onlyTopLevel) {
        runOnAllFilterRowStates(child as FilterGroupState);
      } else {
        const row = child as FilterRowStateNotNull;
        if (row.attribute === attribute) {
          rowFormatFunction(row);
        }
      }
    });

    return groupState;
  };

  return runOnAllFilterRowStates(filterState);
};

export const getFilterGroupStateTopLevelRowAttributes = (
  groupState?: FilterGroupState
): string[] =>
  groupState?.children
    ? groupState.children
        .filter(({ type }) => type === "row")
        .filter((child) => (child as FilterRowState).attribute)
        .map((child) => (child as FilterRowStateNotNull).attribute)
    : [];

/**
 * Returns the top-level row with the given attribute.
 */
export const getTopLevelRowFromFilterGroupState = (
  attribute: string,
  filters?: FilterGroupState
): FilterRowStateNotNull | undefined => {
  if (!filters) return;

  const foundChildren = filters.children.filter(
    (child) => child.type === "row" && child.attribute === attribute
  ) as FilterRowState[];

  if (!foundChildren.length) return;

  return foundChildren[0] as FilterRowStateNotNull;
};

export const removeAttributesFromFilterGroupState = (
  filters: FilterGroupState,
  attributesToRemove: string[]
): FilterGroupState => {
  const copy = cloneObject(filters);

  attributesToRemove.forEach((attribute) => {
    // remove all type="row" objects with attribute = key
    const removeKey = (group: FilterGroupState) => {
      group.children = group.children.filter((child) => {
        if (child.type === "row") {
          return child.attribute !== attribute;
        } else {
          removeKey(child);

          return true;
        }
      });
    };
    removeKey(copy);
  });

  if (copy.children.length === 0) return DEFAULT_FILTER_BUILDER_STATE;

  return copy;
};

export const removeAttributesWithPrefixFromFilterGroupState = (
  filters: FilterGroupState,
  prefix: string
): FilterGroupState => {
  const copy = cloneObject(filters);

  // remove all type="row" objects with attribute that starts with prefix
  const removeKey = (group: FilterGroupState) => {
    group.children = group.children.filter((child) => {
      if (child.type === "row") {
        return !child.attribute?.startsWith(prefix);
      } else {
        removeKey(child);

        return true;
      }
    });
  };

  removeKey(copy);

  if (copy.children.length === 0) return DEFAULT_FILTER_BUILDER_STATE;

  return copy;
};

export const isDefaultAdvancedFilterState = (
  filterState?: FilterGroupState,
  checkId = true
): boolean =>
  checkId
    ? DEFAULT_FILTER_BUILDER_STATE.id === filterState?.id &&
      (DEFAULT_FILTER_BUILDER_STATE.anyAll === filterState?.anyAll ||
        DEFAULT_FILTER_BUILDER_STATE_ANY.anyAll === filterState.anyAll) &&
      DEFAULT_FILTER_BUILDER_STATE.type === filterState?.type &&
      filterState?.children?.length === 0
    : (DEFAULT_FILTER_BUILDER_STATE.anyAll === filterState?.anyAll ||
        DEFAULT_FILTER_BUILDER_STATE_ANY.anyAll === filterState?.anyAll) &&
      DEFAULT_FILTER_BUILDER_STATE.type === filterState?.type &&
      filterState?.children?.length === 0;

export const addRowFilterGroupState = (
  filters: FilterGroupState | undefined,
  rowToAdd?: FilterRowStateNotNull
): FilterGroupState => {
  const copy = cloneObject(filters || DEFAULT_FILTER_BUILDER_STATE);

  if (!rowToAdd) return copy;

  copy.children?.push(rowToAdd);

  return copy;
};

/**
 * Updates or adds a row to the filter group state based on the attribute.
 * Only works for top-level children.
 */
export const updateOrAddRowFilterGroupState = (
  filters: FilterGroupState | undefined,
  rowToAddOrUpdate?: FilterRowStateNotNull
): FilterGroupState => {
  const copy = cloneObject(filters || DEFAULT_FILTER_BUILDER_STATE);

  if (!rowToAddOrUpdate) return copy;

  const currentAttribute = rowToAddOrUpdate.attribute;

  // if current pending state is default, then update existing row-0
  if (isDefaultAdvancedFilterState(copy)) {
    copy.children[0] = rowToAddOrUpdate;

    return copy;
  }

  // if not default, then check if the attribute already exists
  const foundChildRow = copy.children?.find(
    (child) => child.type === "row" && child.attribute === currentAttribute
  );

  // if it does, update it
  if (foundChildRow) {
    let isReplaced = false;
    copy.children = copy.children
      ?.map((child) => {
        if (child.type === "row" && child.attribute === currentAttribute) {
          if (isReplaced) return undefined;

          isReplaced = true;

          return rowToAddOrUpdate;
        }

        return child;
      })
      .filter(Boolean) as FilterGroupState["children"];
  } else {
    // if it doesn't, add it
    copy.children?.push(rowToAddOrUpdate);
  }

  return copy;
};

export const getValuesForAttributeFromFilterState = (
  attribute: string,
  filterState: FilterGroupState
): string[] => {
  // Only search for attribute in the top-level group's children
  const foundChild = filterState.children.find(
    (child) => child.type === "row" && child.attribute === attribute
  );

  return foundChild ? (foundChild as FilterRowState).values || [] : [];
};

export const getFiltersForAttributeFromFilterState = (
  attribute: string,
  filterState: FilterGroupState
): (FilterRowState | FilterGroupState | undefined)[] => {
  // Only search for attribute in the top-level group's children
  const foundChildren = filterState.children.filter(
    (child) => child.type === "row" && child.attribute === attribute
  );

  return foundChildren ?? [];
};

interface FilterRowRelations {
  parent: string | null;
  siblingBefore: string | null;
  siblingAfter: string | null;
}

export const getFilterRowRelations = (
  filterState: FilterGroupState,
  id: string,
  parent: string | null = null,
  depth = 0
): FilterRowRelations | undefined => {
  const relationships: FilterRowRelations = {
    parent: depth === 0 ? filterState.id : parent,
    siblingBefore: null,
    siblingAfter: null,
  };

  for (let i = 0; i < filterState.children.length; i++) {
    const child = filterState.children[i];
    if (child.id === id) {
      if (i > 0) {
        relationships.siblingBefore = filterState.children[i - 1].id;
      }

      if (i < filterState.children.length - 1) {
        relationships.siblingAfter = filterState.children[i + 1].id;
      }

      return relationships;
    }

    if (child.type === "group") {
      const result = getFilterRowRelations(child, id, child.id, depth + 1);
      if (result) {
        return result;
      }
    }
  }

  return;
};

/**
 * Gets all unique attributes used in the filter state.
 */
export const getAllAttributesFromFilterGroupState = (
  filterState: FilterGroupState
): string[] => {
  const attributes = new Set<string>();

  const traverse = (state: FilterGroupState) => {
    state.children.forEach((child) => {
      if (child.type === "row" && child.attribute) {
        attributes.add(child.attribute);
      } else if (child.type === "group") {
        traverse(child);
      }
    });
  };

  traverse(filterState);

  return Array.from(attributes);
};

export const removeInvalidAttributes = (
  filters: FilterGroupState,
  schemaAttributes: string[] = []
): FilterGroupState | null => {
  const attributesInFilter = getAllAttributesFromFilterGroupState(filters);
  const invalidAttributes = attributesInFilter.filter(
    (attr) => !schemaAttributes.includes(attr)
  );

  if (invalidAttributes.length) {
    return removeAttributesFromFilterGroupState(filters, invalidAttributes);
  }

  return null;
};

/**
 * Drops quick-filter rows whose attribute is no longer valid. In-progress rows
 * (attribute still null) are kept. Returns null when nothing was removed.
 */
export const removeInvalidQuickFilters = (
  quickFilters: FilterRowState[],
  filterableAttributes: string[]
): FilterRowState[] | null => {
  const cleaned = quickFilters.filter(
    (row) =>
      row.attribute == null || filterableAttributes.includes(row.attribute)
  );

  return cleaned.length === quickFilters.length ? null : cleaned;
};

/**
 * Drops sort keys that are no longer sortable. Returns undefined when nothing
 * was removed, otherwise the cleaned sort (which may be empty).
 */
export const removeInvalidSortKeys = (
  sort: SortBy | undefined,
  sortableAttributes: string[]
): SortBy | undefined => {
  if (!sort) return undefined;

  const validEntries = Object.entries(sort).filter(([key]) =>
    sortableAttributes.includes(key)
  );

  return validEntries.length === Object.keys(sort).length
    ? undefined
    : Object.fromEntries(validEntries);
};

export const createFilterGroupStateFromFilterRowStateArray = (
  filterRowStates: FilterRowState[] | undefined
): FilterGroupState | undefined => {
  if (!filterRowStates) return undefined;

  return {
    id: `group-${randomID()}`,
    type: "group",
    anyAll: "all",
    children: filterRowStates,
  };
};
